import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Truck, Sparkles, Handshake, Award, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";
import AnimatedCounter from "@/components/AnimatedCounter";

const CATEGORY_SPANS = {
  televisions: "md:col-span-2 md:row-span-2",
  "air-conditioners": "md:col-span-2",
  "washing-machines": "md:col-span-1",
  "chest-freezers": "md:col-span-1",
  "air-coolers": "md:col-span-2",
};

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products?featured=true").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-line">
        {/* Background banner image */}
        <div className="absolute inset-0">
          <img
            src="/assets/hero/hero-bg.jpg"
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#fbf9f6] via-[#fbf9f6]/90 to-[#fbf9f6]/20" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#fbf9f6]" />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-32 lg:px-10">
          <div className="max-w-3xl fade-up">
            <div className="overline mb-6">Energizing Life · Made for Nepal</div>
            <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tight text-ink md:text-7xl lg:text-[92px]">
              World-class technology
              <br />
              <span className="crimson-underline text-[#dd0447]">tailored for Nepal.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-ink-2 md:text-lg">
              AREL Nepal Pvt Ltd — home appliances engineered globally, tuned
              locally. Smart LED TVs, air conditioners, washing machines, chest
              freezers and air coolers built for how Nepal lives, and now the
              official importer of Daikin Air Conditioners.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/products"
                data-testid="hero-explore-btn"
                className="arel-primary-btn inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
              >
                Explore Products <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/daikin"
                data-testid="hero-daikin-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-[#0097e0]/40 px-7 py-3.5 text-sm font-semibold text-[#0097e0] transition-colors duration-300 hover:bg-[#e6f6fe]"
              >
                <img src="/assets/logo/daikin-logo.png" alt="Daikin" className="h-5 w-auto" />
                Daikin ACs
              </Link>
              <Link
                to="/dealer"
                data-testid="hero-dealer-btn"
                className="arel-outline-btn inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
              >
                Become a Dealer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ANIMATED STATS */}
      <section className="border-b border-line bg-[#f4f1ec] py-16 md:py-20" data-testid="home-stats-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-8 sm:grid-cols-3">
            {[
              { label: "Happy Customers", value: "1,000,000+", testId: "stat-happy-customers" },
              { label: "Cities Covered", value: "25+", testId: "stat-cities-covered" },
              { label: "Channel Partners", value: "200+", testId: "stat-channel-partners" },
            ].map((s) => (
              <div key={s.label} className="text-center md:text-left">
                <AnimatedCounter
                  target={s.value}
                  testId={s.testId}
                  className="font-display text-5xl font-black leading-none text-ink md:text-6xl lg:text-7xl"
                />
                <div className="mt-3 text-xs font-bold uppercase tracking-[0.28em] text-muted-warm">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND STRIP */}
      <section className="border-b border-line bg-arel-bg py-6 overflow-hidden">
        <div className="marquee-track flex gap-16 whitespace-nowrap">
          {[..."ENERGY EFFICIENT · NEPAL SERVICE NETWORK · 10-YEAR COMPRESSOR WARRANTY · MADE FOR MONSOON · OFFICIAL DAIKIN IMPORTER · ".repeat(3)].join("").split("·").map((s, i) => (
            <span key={i} className="font-display text-xl font-black uppercase tracking-widest text-[#c7bfb0]">
              {s.trim()} <span className="text-[#dd0447]">·</span>
            </span>
          ))}
        </div>
      </section>

      {/* CATEGORY BENTO */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="home-categories-section">
        <div className="mb-14 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="overline mb-4">Product Universe</div>
            <h2 className="font-display text-4xl font-black tracking-tight text-ink md:text-5xl">
              Five categories.
              <br />
              One promise.
            </h2>
          </div>
          <Link
            to="/products"
            data-testid="home-view-all-products-link"
            className="arel-outline-btn rounded-full px-5 py-2.5 text-sm font-semibold"
          >
            View all products
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4 md:auto-rows-[220px]">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/products?category=${cat.slug}`}
              data-testid={`category-card-${cat.slug}`}
              className={`arel-card group relative overflow-hidden rounded-xl ${CATEGORY_SPANS[cat.slug] || "md:col-span-1"}`}
            >
              <img
                src={cat.image_url}
                alt={cat.name}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
              <div className="relative flex h-full min-h-[220px] flex-col justify-end p-6">
                <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/80">Category</div>
                <div className="mt-2 font-display text-2xl font-bold text-white md:text-3xl">
                  {cat.name}
                </div>
                <div className="mt-3 inline-flex items-center gap-2 text-sm text-white/85 transition-colors duration-300 ease-out group-hover:text-white">
                  Explore <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="border-y border-line bg-[#f4f1ec] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="mb-14 flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="overline mb-4">Best in class</div>
              <h2 className="font-display text-4xl font-black tracking-tight text-ink md:text-5xl">
                Featured products
              </h2>
            </div>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {products.filter(p => p.brand === "AREL").slice(0, 4).map((p) => (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                data-testid={`featured-product-card-${p.id}`}
                className="arel-card group flex flex-col overflow-hidden rounded-xl"
              >
                <div className="relative aspect-square overflow-hidden bg-[#efe9df]">
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-col gap-2 p-5">
                  <div className="overline">{p.model}</div>
                  <div className="font-display text-lg font-bold leading-tight text-ink">
                    {p.name}
                  </div>
                  <div className="text-sm text-muted-warm line-clamp-2">
                    {p.short_description}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-[#dd0447]">
                    Inquire now <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY AREL */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <div className="grid gap-16 md:grid-cols-2 md:items-center">
          <div>
            <div className="overline mb-4">The AREL edge</div>
            <h2 className="font-display text-4xl font-black tracking-tight text-ink md:text-5xl">
              Global engineering.
              <br />
              <span className="text-[#dd0447]">Nepali soul.</span>
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-warm">
              We partner with the best global manufacturers to bring you
              appliances that pass rigorous Nepal-market testing — voltage
              tolerance, monsoon humidity, and long-hour usage.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { icon: ShieldCheck, title: "Quality Assured", desc: "ISO-grade production standards on every unit." },
              { icon: Award, title: "Trusted Partners", desc: "Official Daikin importer + hand-picked global suppliers." },
              { icon: Truck, title: "Nepal-Wide Delivery", desc: "Dealer network across all 7 provinces." },
              { icon: Sparkles, title: "Warranty & Care", desc: "Up to 10-year compressor warranty." },
            ].map((f, i) => (
              <div
                key={i}
                className="arel-card rounded-xl p-6"
                data-testid={`why-arel-card-${i}`}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#dd0447]/10 text-[#dd0447]">
                  <f.icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-display text-lg font-bold text-ink">{f.title}</div>
                <div className="mt-2 text-sm text-muted-warm">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEALER CTA */}
      <section className="relative overflow-hidden border-t border-line bg-[#0b0b0d] py-24 text-white" data-testid="home-dealer-section">
        <div className="absolute -right-40 -top-40 h-96 w-96 rounded-full bg-[#dd0447] blur-[140px] opacity-50" />
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:grid-cols-2 lg:px-10">
          <div>
            <div className="mb-4 text-xs font-bold uppercase tracking-[0.24em] text-[#dd0447]"><Handshake className="mr-2 inline-block h-4 w-4" /> Partnership</div>
            <h2 className="font-display text-4xl font-black tracking-tight md:text-5xl">
              Grow with AREL.
              <br />
              Become a dealer.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/70">
              Join a growing network of AREL dealers across Nepal. Competitive
              margins, marketing support, and reliable supply — from Biratnagar
              to Karnali.
            </p>
            <div className="mt-8 space-y-3 text-sm text-white/70">
              {[
                "Attractive dealer margins",
                "Marketing & branding kits",
                "Priority stock allocation",
                "Regional sales training",
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-[#dd0447]" /> {b}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl bg-white p-8 text-ink">
            <div className="mb-6 font-display text-xl font-bold text-ink">Dealer Inquiry</div>
            <InquiryForm
              inquiryType="dealer"
              subject="Dealer Inquiry"
              testIdPrefix="home-dealer"
              compact
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
